import { useEffect, useMemo, useState } from 'react';
import { useUser } from '../UserProvider';
import { useAppConfig } from '../AppConfigProvider';
import qs from 'qs';

const cache = new Map<string, unknown>();

// use this for things that are triggered by things like application or page or component load
export function useEagerFetch<T>(
  path?: string | null,
  requiresAuth = true,
  shouldCache = true
) {
  const appConfig = useAppConfig();
  const [, { getAuthToken }] = useUser();

  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<T | null>(
    shouldCache && path && cache.has(path) ? (cache.get(path) as T) : null
  );
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    async function fetchData(atPath: string) {
      setData(null);
      setError(null);
      setIsLoading(true);

      try {
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        if (requiresAuth) {
          const authToken = await getAuthToken();
          headers['Authorization'] = `Bearer ${authToken}`;
        }

        const response = await fetch(atPath, {
          headers,
        });
        if (response.status >= 400) {
          throw new Error(`Failed request, status=${response.status}`);
        }

        const responseData: T = await response.json();
        if (shouldCache) {
          cache.set(atPath, responseData);
        }
        setData(responseData);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch data');
      } finally {
        setIsLoading(false);
      }
    }

    if (appConfig.data?.apiUrl && path) {
      const url = `${appConfig.data.apiUrl}${path}`;

      const cachedData = cache.get(path);
      if (shouldCache && cachedData) {
        setData(cachedData as T);
      } else {
        fetchData(url);
      }
    }
  }, [path, requiresAuth, getAuthToken, appConfig, shouldCache]);

  //TODO: should memoize this?
  // return useMemo(() => ({ data, isLoading, error }), [data, isLoading, error]);
  return { data, isLoading, error };
}

export default useEagerFetch;
